import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Input } from '../../ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../ui/form';
import { LoadingSpinner } from '../../shared/ui/LoadingSpinner';
import { emailSchema } from '../../../lib/utils/validation';

interface EmailFormData {
  email: string;
}

interface EmailFormProps {
  onSubmit: (email: string) => void;
  isSendingCode: boolean;
  setInputElement: (element: HTMLInputElement | null) => void;
}

export const EmailForm: React.FC<EmailFormProps> = ({
  onSubmit,
  isSendingCode,
  setInputElement,
}) => {
  const t = useTranslations('AuthWithOTPButton');

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleSubmit = (values: EmailFormData) => {
    onSubmit(values.email);
  };

  return (
    <div className='space-y-4'>
      <div className='space-y-1'>
        <div className='text-sm text-left font-normal text-gray-900 mb-1 text-muted-foreground'>
          {t('email')}
        </div>
        <Form {...emailForm}>
          <form onSubmit={emailForm.handleSubmit(handleSubmit)} className='space-y-4'>
            <FormField
              control={emailForm.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      type='email'
                      placeholder={t('emailPlaceholder')}
                      ref={el => {
                        field.ref(el);
                        setInputElement(el);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button size='lg' type='submit' className='w-full' disabled={isSendingCode}>
              {isSendingCode ? <LoadingSpinner /> : t('continueWithEmail')}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};
